import {
  computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { QR_CODE_TOKEN, USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM } from '../constants/settings';
import { Gender, PAGE_SIZE } from '../constants/options';
import { ProfileTemplate } from '../utilities/profile';
import { GetApiLang, GetLangKey, GetUserProfile, Language } from '../utilities/general';
import { FAxios } from '../utilities/file-network';

export default class PatientStore {

  loading: boolean = false;

  loadingHistory: boolean = false;
  loadingReport: boolean = false;


  loadingFiles: boolean = false;

  creating: boolean = false;

  updating: boolean = false;

  uploading: boolean = false;

  downloading: boolean = false;

  removing: boolean = false;

  copying: boolean = false;

  processingHistory: boolean = false;

  getOptions: boolean = false;

  loadingCameraToken: boolean = false;

  loadingTokenWebInfo: boolean = false;

  loadingTokenPhoneInfo: boolean = false;

  uploadingCameraAttachments: boolean = false;

  consentTokenGenerating: boolean = false;

  uploadingPatientConsent: boolean = false;

  downloadingAllConsentForms: boolean = false;

  historyCreatedAt: { id: number, createdAt: string } & any = null;

  updatingDate: boolean = false;

  loadingFamilyHistory: boolean = false;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      loading: observable,
      loadingHistory: observable,
      creating: observable,
      updating: observable,
      uploading: observable,
      downloading: observable,
      removing: observable,
      processingHistory: observable,
      getOptions: observable,
      copying: observable,
      loadingCameraToken: observable,
      loadingFiles: observable,
      loadingTokenWebInfo: observable,
      loadingTokenPhoneInfo: observable,
      uploadingCameraAttachments: observable,
      loadingReport: observable,
      consentTokenGenerating: observable,
      uploadingPatientConsent: observable,
      downloadingAllConsentForms: observable,
      historyCreatedAt: observable,
      updatingDate: observable,
      loadingFamilyHistory: observable,
    });
    this.rootStore = rootStore;
  }


  searchPatient = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Patient/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, clinicId: GetUserProfile().currentClinicId });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchPatientBlur = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}) => {
    try {
      const res = await Axios.post(`/api/Patient/BlurSearch?platformId=${CURRENT_PLATFORM}`, { ...reqModel });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  getPatient = async (id: number, lang: Language = Language.ENGLISH) => {
    this.loading = true;
    try {
      const clinicId = GetUserProfile()?.currentClinicId;
      const res = await Axios.get(`/api/Patient/${id}?clinicId=${clinicId}&lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  addPatient = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.creating = true;
    try {
      const res = await Axios.post(`/api/Patient?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creating = false;
    }
  };

  updatePatient = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.updating = true;
    try {
      const res = await Axios.put(`/api/Patient?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  };

  deletePatient = async (id: number) => {
    this.removing = true;
    try {
      const res = await Axios.delete(`/api/Patient/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };

  mergePatient = async (reqModel: any) => {
    this.removing = true;
    try {
      const res = await Axios.put(`/api/Patient/Merge?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };

  uploadPatientAttachment = async (reqModel: any) => {
    this.uploading = true;
    try {
      const res = await Axios.post(`/api/Patient/Attachment?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploading = false;
    }
  };

  deletePatientAttachment = async (id: number) => {
    this.removing = true;
    try {
      const res = await Axios.delete(`/api/Patient/Attachment/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };

  searchPatientHistory = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
    if (!reqModel.noLoading) this.loadingHistory = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, clinicId: GetUserProfile().currentClinicId });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  createPatientHistory = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.processingHistory = true;
    try {
      const res = await Axios.post(`/api/Patient/History?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.processingHistory = false;
    }
  };

  updatePatientHistory = async (reqModel: any, isLoadingDisabled: boolean = false, lang: Language = GetApiLang()) => {
    // this.updating = true;
    if (!isLoadingDisabled) this.processingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      // this.updating = false;
      if (!isLoadingDisabled) this.processingHistory = false;
    }
  };

  removeMultiplePatientHistory = async (ids: number[]) => {
    this.removing = true;
    let req = { ids: ids };
    try {
      const clinicId = GetUserProfile()?.currentClinicId || 0;
      const res = await Axios.post(`/api/Patient/History/Delete?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };


  removePatientHistory = async (id: number) => {
    this.removing = true;
    try {
      const clinicId = GetUserProfile()?.currentClinicId || 0;
      const res = await Axios.delete(`/api/Patient/History/${id}?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };

  getPatientHistory = async (id: number, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Patient/History/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // In-flight cache for prefetched history-diagnosis requests. Lets callers
  // (e.g. patient-info row "Edit") kick off the fetch before navigation so the
  // /prescription/form page can reuse the in-flight Promise instead of issuing
  // a second identical request after mount. Entries auto-expire so stale data
  // can't leak into a later visit.
  private _historyDiagnosisCache = new Map<number, Promise<any>>();
  private static HISTORY_DIAGNOSIS_TTL_MS = 30_000;

  prefetchHistoryDiagnosis = (id: number) => {
    if (this._historyDiagnosisCache.has(id)) return;
    const promise = this._fetchHistoryDiagnosis(id);
    this._historyDiagnosisCache.set(id, promise);
    const drop = () => {
      if (this._historyDiagnosisCache.get(id) === promise) {
        this._historyDiagnosisCache.delete(id);
      }
    };
    // Drop on failure immediately so a retry can refetch; drop on success after
    // TTL so a quick second navigation can still skip the round-trip.
    promise.then(
      () => setTimeout(drop, PatientStore.HISTORY_DIAGNOSIS_TTL_MS),
      () => drop()
    );
  };

  getPatientHistoryDiagnosis = async (id: number) => {
    const cached = this._historyDiagnosisCache.get(id);
    if (cached) {
      this._historyDiagnosisCache.delete(id);
      return cached;
    }
    return this._fetchHistoryDiagnosis(id);
  };

  private _fetchHistoryDiagnosis = async (id: number) => {
    this.historyCreatedAt = null;
    this.loading = true;
    try {
      const clinicId = GetUserProfile()?.currentClinicId || 0;
      const res = await Axios.get(`/api/Patient/History/Diagnosis/${id}?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`);
      this.historyCreatedAt = { id, createdAt: res.data.createdAt };
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  uploadHistoryAttachments = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.uploading = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Attachments?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploading = false;
    }
  };

  searchHistoryAttachments = async (skip: number, take: number, reqModel: any = {}) => {
    this.loadingFiles = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Attachments/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingFiles = false;
    }
  };

  removeHistoryAttachment = async (id: number) => {
    this.removing = true;
    try {
      const res = await Axios.delete(`/api/Patient/History/Attachment/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };

  searchAllergenOptions = async (reqModel: any = {}) => {
    this.getOptions = true;
    try {
      const res = await Axios.post(`/api/Option/Allergen/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.getOptions = false;
    }
  };

  searchSymptomsOptions = async (reqModel: any = {}) => {
    this.getOptions = true;
    try {
      const res = await Axios.post(`/api/Option/Symptom/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.getOptions = false;
    }
  };


  copyPatientHistory = async (preId: number, practitionerId: number) => {
    this.copying = true;
    try {
      const clinicId = GetUserProfile()?.currentClinicId || 0;
      const res = await Axios.post(`/api/Patient/History/Copy?preId=${preId}&clinicId=${clinicId}&practitionerId=${practitionerId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.copying = false;
    }
  };


  searchPatientHistoryPayment = async (skip: number, take: number, reqModel: any) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Payment/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, { ...reqModel });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };


  getComplaintByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/Complaint?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updateComplaintByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/Complaint?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };
  //Create new history
  createDiagnosis = async (reqModel: any) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Diagnosis?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  // Fire-and-forget usage ping (e.g. one per prescription created, tagged
  // classic vs guided) — callers should not await/block on this or surface
  // its errors, it's purely a background count.
  addStatisticCount = async (category: string) => {
    try {
      const res = await Axios.put(`/api/Statistic/Count`, { category });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  getAuxiliaryTestByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/AuxiliaryTest?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updateAuxiliaryTestByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/AuxiliaryTest?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  getPreviousHistoryByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/PreviousHistory?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updatePreviousHistoryByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/PreviousHistory?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  getPersonalHealthByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/PersonalHealth?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updatePersonalHealthByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/PersonalHealth?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  getSummaryByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/Summary?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updateSummaryByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/Summary?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  // generateCameraToken = async (req: any) => {
  //   this.loadingCameraToken = true;
  //   try {
  //     const res = await Axios.post(`/api/Patient/History/Camera/Token?platformId=${CURRENT_PLATFORM}`, req);
  //     SetLocalStorage(QR_CODE_TOKEN, { ...res.data, localCreateTime: Date.now() });
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.loadingCameraToken = false;
  //   }
  // };

  GenerateCameraToken = async (
    historyId: number,
    sectionKey: string,
    valueKey: string | number,
    targetId?: number
  ) => {
    this.loadingCameraToken = true;
    try {
      const updateRep: any = {
        patientHistoryId: historyId,
        diagnosisSession: sectionKey,
        diagnosisValue: String(valueKey),
        targetObjectId: targetId,
      };
      const localQRToken: any = await GetLocalStorage(QR_CODE_TOKEN);
      // console.log('GenerateCameraToken localQRToken', localQRToken, localQRToken.patientHistoryId === Number(historyId));
      if ((localQRToken && Date.now() - localQRToken.localCreateTime <= 30 * 60 * 1000) && localQRToken?.patientHistoryId === Number(historyId)) {
        const uploadRes = await Axios.put(`/api/Patient/History/Camera/Token?token=${localQRToken.token}&platformId=${CURRENT_PLATFORM}`, updateRep);
        const newLocalQRToken = { ...localQRToken, ...uploadRes };
        SetLocalStorage(QR_CODE_TOKEN, newLocalQRToken);
        return Promise.resolve(newLocalQRToken);
      } else {
        const res = await Axios.post(`/api/Patient/History/Camera/Token?platformId=${CURRENT_PLATFORM}`, updateRep);
        SetLocalStorage(QR_CODE_TOKEN, { ...res.data, localCreateTime: Date.now() });
        return Promise.resolve(res.data);
      }
    } catch (err) {
      SetLocalStorage(QR_CODE_TOKEN, null);
      return Promise.reject(err);
    } finally {
      this.loadingCameraToken = false;
    }
  };

  ClearLocalCameraToken = async () => {
    this.loadingCameraToken = true;
    try {
      SetLocalStorage(QR_CODE_TOKEN, null);
      return Promise.resolve('OK');
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingCameraToken = false;
    }
  };

  updateTokenInfo = async (token: string, diagnosisSession: string, diagnosisValue: string, targetObjectId?: number) => {
    this.loadingTokenWebInfo = true;
    let req: any = { diagnosisSession, diagnosisValue };
    if (targetObjectId) req.targetObjectId = targetObjectId;
    try {
      const res = await Axios.put(`/api/Patient/History/Camera/Token?token=${token}&platformId=${CURRENT_PLATFORM}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingTokenWebInfo = false;
    }
  };


  getCameraWebInfoByToken = async (token: string, diagnosisName: string = "Examination") => {
    this.loadingTokenWebInfo = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Camera/${diagnosisName}/WebInfo?token=${token}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingTokenWebInfo = false;
    }
  };

  getCameraPhoneInfoByToken = async (token: string, diagnosisName: string = "") => {
    this.loadingTokenPhoneInfo = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Camera/${diagnosisName}/PhoneInfo?token=${token}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingTokenPhoneInfo = false;
    }
  };

  updateCameraTokenText = async (token: string, diagnosisName: string = "", req: any) => {
    this.loadingTokenPhoneInfo = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Camera/${diagnosisName}/Text?token=${token}&platformId=${CURRENT_PLATFORM}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingTokenPhoneInfo = false;
    }
  };

  uploadCameraAttachments = async (reqModel: any, token: string) => {
    this.uploadingCameraAttachments = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Camera/Attachment?token=${token}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingCameraAttachments = false;
    }
  };


  getPatientHistoryPaymentReport = async (reqModel: any) => {
    this.loadingReport = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Payment/Report?platformId=${CURRENT_PLATFORM}`, { ...reqModel });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingReport = false;
    }
  };

  generateConsentToken = async (id: number, clinicId: number, signatureDate?: string) => {
    this.consentTokenGenerating = true;
    try {
      const res = await Axios.post(`/api/Patient/Consent/Form/Token`, { patientId: id, clinicId, paperSignedDate: signatureDate });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.consentTokenGenerating = false;
    }
  };

  generateSignatureConsent = async (patientId: number, clinicId: number) => {
    this.creating = true;
    try {
      const res = await FAxios.get(`/api/Patient/Consent/Form/${patientId}/${clinicId}?lang=${GetApiLang()}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creating = false;
    }
  };

  uploadConsentFormFile = async (reqModel: any) => {
    this.uploading = true;
    try {
      const res = await Axios.post(`/api/Patient/Consent/Form/Upload`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploading = false;
    }
  };

  searchPatientAttachment = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Patient/Attachment/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  uploadPatientConsent = async (reqModel: any) => {
    this.uploadingPatientConsent = true;
    try {
      const res = await Axios.post(`/api/Patient/Consent/Form/Generate?lang=${GetApiLang()}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingPatientConsent = false;
    }
  };

  downloadAllConsentForms = async (patientId: number, clinicId: number) => {
    this.downloadingAllConsentForms = true;
    try {
      const res = await FAxios.post(`/api/Patient/Consent/Form/Word/Zip`, { patientId, clinicId }, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloadingAllConsentForms = false;
    }
  };

  updatePrescriptionDate = async (patientHistoryId: number, clinicId: number, reqModel: any) => {
    this.updatingDate = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/Date?patientHistoryId=${patientHistoryId}&clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updatingDate = false;
    }
  };

  exportPatientExcel = async () => {
    this.downloading = true;
    try {
      const res = await FAxios.get(`/api/Patient/Excel/Export/Empty?platformId=${CURRENT_PLATFORM}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  importPatientExcel = async (reqModel: FormData) => {
    this.uploading = true;
    try {
      const res = await FAxios.post(`/api/Patient/Excel/Import?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploading = false;
    }
  };

  updateImportedPatientExcel = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Patient/Excel/Import?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  exportPatientHistoryExcel = async (reqModel: any) => {
    this.downloading = true;
    try {
      const res = await FAxios.post(`/api/Patient/History/Diagnosis/Excel?platformId=${CURRENT_PLATFORM}`, reqModel, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  searchMedicalHistoryFamily = async (id: number, skip: number, take: number = PAGE_SIZE) => {
    this.loadingFamilyHistory = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Search/Family/${id}/${skip}/${take}?lang=${GetApiLang()}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingFamilyHistory = false;
    }
  };

}